import User from '@dehaat/kisan-app-bl/models/User';
import { useRouter } from 'next/router';
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { CART_ERRORS } from '@/constants/cart';
import { CAMPAIGN_CART } from '@/constants/common';
import { DELIVERY_TYPE } from '@/models/Cart';
import { PriceInStorage } from '@/models/Product';
import createCart from '@/utils/api/createCart';
import deleteCart from '@/utils/api/deleteCart';
import { updateCampaignCartIdInLocalStorage as updateCartIdInLocalStorage } from '@/utils/cart';
import { getLocalStorageKey, removeLocalStorageKey, setLocalStorageKey } from '@/utils/helper';
import { AuthContext } from './AuthProvider';
interface CartUpdateParams {
  variantId: string;
  quantity: number;
  index?: number;
  variantPrice: number;
  variantDiscount: number;
  name?: string;
  brandName?: string;
  isCombo?: boolean;
  parentType?: string;
}
interface Context {
  cartId: string;
  campaignCart: Record<string, number>;
  updateCart: (params: CartUpdateParams, isAdd: boolean) => void;
  deliveryType?: DELIVERY_TYPE;
  campaignComboCart: Record<string, number>;
  setDeliveryType: Dispatch<SetStateAction<DELIVERY_TYPE | undefined>>;
  isPickupOrder: boolean;
  pickupFarmerDetails?: User;
  setPickupFarmerDetails: (farmer: User | undefined) => void;
  resetCart: (skipDeleteCart?: boolean) => void;
  updateCartState: (key: keyof Cart, value: unknown) => void;
}
const cartInitialState: Cart = {
  cartId: '',
  items: {},
  pickupFarmerDetails: undefined,
  comboItems: {}
};
export const CampaignContext = createContext<Context>({
  cartId: '',
  campaignCart: {},
  updateCart: () => undefined,
  deliveryType: DELIVERY_TYPE.HOME_DELIVERY,
  campaignComboCart: {},
  setDeliveryType: () => {
    /**/
  },
  isPickupOrder: false,
  pickupFarmerDetails: undefined,
  resetCart: () => undefined,
  setPickupFarmerDetails: () => undefined,
  updateCartState: () => undefined
});
export interface Cart {
  items: Record<string, number>;
  comboItems: Record<string, number>;
  cartId: string;
  pickupFarmerDetails?: User;
}
const CampaignProvider = ({
  children
}: PropsWithChildren<Record<never, never>>) => {
  const [cartItems, setCartItems] = useState<Cart>(cartInitialState);
  const {
    cartId,
    pickupFarmerDetails,
    comboItems,
    items
  } = cartItems;
  const {
    asPath,
    query,
    pathname,
    reload
  } = useRouter();
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const [deliveryType, setDeliveryType] = useState<DELIVERY_TYPE | undefined>(DELIVERY_TYPE.HOME_DELIVERY);
  const updateCartToLocalStorage = useCallback((cart: Cart) => {
    setLocalStorageKey(CAMPAIGN_CART, cart);
  }, []);
  const updateCartToBE = useCallback(async (params: CartUpdateParams, newQuantity: number, cartId?: string, retryCount = 1, shouldOverrideCart: boolean = false) => {
    if (!isAuthenticated) {
      const data: PriceInStorage = getLocalStorageKey('cpp_d', {}) || {};
      data[params.variantId] = {
        price: params.variantPrice,
        discount: params.variantDiscount
      };
      setLocalStorageKey('cpp_d', data);
      return;
    }
    if (pathname.includes('/cart')) {
      return;
    }
    const {
      variantId,
      variantPrice,
      variantDiscount,
      isCombo,
      parentType
    } = params;
    const product_details = isCombo ? [{
      price: variantPrice,
      discount: variantDiscount,
      quantity: newQuantity,
      sellable_id: Number(variantId),
      parent_type: parentType,
      sellable_type: 'COMBO_VARIANT'
    }] : [{
      price: variantPrice,
      discount: variantDiscount,
      quantity: newQuantity,
      sellable_id: Number(variantId),
      sellable_type: 'PRODUCT_VARIANT'
    }];
    try {
      const res = await createCart({
        product_details,
        is_login_request: shouldOverrideCart,
        cart_id: cartId || '',
        cart_type: 'campaign',
        campaign_id: query.campaignId as string
      });
      if (res?.cart_id && res?.cart_id !== cartId) {
        setCartItems(prev => {
          return {
            ...prev,
            cartId: res?.cart_id
          };
        });
        updateCartIdInLocalStorage(res.cart_id);
      }
    } catch (e: any) {
      let error = e?.data?.error;
      let errorCode = error?.code;
      if (e.error) {
        error = e.error;
        errorCode = error?.code;
      }
      const shouldRetry = (errorCode === CART_ERRORS.CART_401 || errorCode === CART_ERRORS.CART_404) && retryCount === 1;
      if (shouldRetry) {
        return updateCartToBE(params, newQuantity, '', 0);
      }
    }
  }, [isAuthenticated, pathname, query.campaignId]);
  const addToCart = useCallback((params: CartUpdateParams) => {
    const {
      variantId,
      quantity,
      isCombo
    } = params;
    setCartItems(prev => {
      const updatedItems = {
        ...prev.items
      };
      const updatedComboItems = {
        ...prev.comboItems
      };
      if (isCombo) {
        const newComboQty = (updatedComboItems[variantId] || 0) + quantity;
        updatedComboItems[variantId] = newComboQty;
        updateCartToBE(params, newComboQty, prev.cartId || '');
      } else {
        const newQuantity = (updatedItems[variantId] || 0) + quantity;
        updatedItems[variantId] = newQuantity;
        updateCartToBE(params, newQuantity, prev.cartId || '');
      }
      const updatedCart = {
        ...prev,
        items: !isCombo ? updatedItems : {},
        comboItems: isCombo ? updatedComboItems : {}
      };
      updateCartToLocalStorage(updatedCart);
      return updatedCart;
    });
  }, [updateCartToBE, updateCartToLocalStorage]);
  const removeFromCart = useCallback((params: CartUpdateParams) => {
    const {
      variantId,
      quantity,
      isCombo
    } = params;
    setCartItems(prev => {
      const targetKey = isCombo ? 'comboItems' : 'items';
      const updatedMap = {
        ...prev[targetKey]
      };
      const newQuantity = (updatedMap[variantId] || 0) - quantity;
      updateCartToBE(params, newQuantity, prev.cartId || '');
      if (newQuantity > 0) {
        updatedMap[variantId] = newQuantity;
      } else {
        delete updatedMap[variantId];
      }
      const updatedCart = {
        ...prev,
        [targetKey]: updatedMap
      };
      updateCartToLocalStorage(updatedCart);
      return updatedCart;
    });
  }, [updateCartToBE, updateCartToLocalStorage]);
  const resetCart = useCallback((skipDeleteCart = false) => {
    if (cartId && !skipDeleteCart) {
      deleteCart(cartId);
      updateCartIdInLocalStorage('');
    }
    updateCartToLocalStorage({
      items: {},
      comboItems: {},
      cartId: ''
    });
    removeLocalStorageKey('cpp_d');
    setCartItems({
      cartId: '',
      items: {},
      comboItems: {}
    });
  }, [cartId, updateCartToLocalStorage]);
  const updateCart = useCallback((params: CartUpdateParams, isAddToCart: boolean) => {
    return isAddToCart ? addToCart(params) : removeFromCart(params);
  }, [addToCart, removeFromCart]);
  const setPickupFarmerDetails = useCallback((user: User | undefined) => setCartItems(prev => ({
    ...prev,
    pickupFarmerDetails: user
  })), [setCartItems]);
  const updateCartState = useCallback((key: keyof Cart, value: unknown) => {
    setCartItems(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);
  useEffect(() => {
    const cartData = getLocalStorageKey<Cart, null>(CAMPAIGN_CART, null);
    if (cartData === null) {
      updateCartToLocalStorage({
        items: {},
        comboItems: {},
        cartId: ''
      });
    } else {
      setCartItems({
        items: cartData?.items || {},
        comboItems: cartData?.comboItems || {},
        cartId: cartData?.cartId || ''
      });
    }
  }, [updateCartToLocalStorage]);
  return <CampaignContext.Provider value={{
    cartId: cartId || '',
    campaignCart: items,
    updateCart,
    deliveryType,
    campaignComboCart: comboItems,
    setDeliveryType,
    isPickupOrder: deliveryType === DELIVERY_TYPE.SELF_PICKUP,
    resetCart,
    pickupFarmerDetails,
    setPickupFarmerDetails: setPickupFarmerDetails,
    updateCartState
  }} data-sentry-element="unknown" data-sentry-component="CampaignProvider" data-sentry-source-file="CampaignProvider.tsx">
      {children}
    </CampaignContext.Provider>;
};
export default CampaignProvider;